import { Dialog, DialogChainObject } from "quasar"
import { i18n } from "src/boot/i18n"
import SfxImageViewOverlay from "src/components/overlay/SfxImageViewOverlay.vue"
import SfxOverlay from "src/components/overlay/SfxOverlay.vue"
import SfxOverlayNotifications from "src/components/overlay/SfxOverlayNotifications.vue"
import { ItemDocument } from "src/db/collectionDefinitions/csi/item"
import SfxPhotoGalleryOverlay from "src/pages/manager/overlay/SfxPhotoGalleryOverlay.vue"
import SfxWorkOrderEntityAssignment from "src/pages/manager/overlay/SfxWorkOrderEntityAssignment.vue"
import ChangePasswordOverlay from "src/pages/technician/overlay/ChangePasswordOverlay.vue"
import ChangeUnlockKeyOverlay from "src/pages/technician/overlay/ChangeUnlockKeyOverlay.vue"
import ConfigurationsOverlay from "src/pages/technician/overlay/ConfigurationsOverlay.vue"
import IdentifyOverlay from "src/pages/technician/overlay/IdentifyOverlay.vue"
import IssueCreateOverlay from "src/pages/technician/overlay/IssueCreateOverlay.vue"
import ItemCreateOverlay from "src/pages/technician/overlay/ItemCreateOverlay.vue"
import ModelClassOverlay from "src/pages/technician/overlay/ModelClassOverlay.vue"
import { ContextSource } from "src/util/enums/contextSource"
import { canUpdateEntityAttachments } from "src/util/functions/attachments"
import { getEntityWithDesignation, getIdentifyTitle } from "src/util/functions/identifier"
import { ImageGalleryItem, ImageViewerItem } from "src/util/types/components/sfxImageGallery"
import { ProgressIndicatorState } from "src/util/types/components/sfxProgressIndicator"
import { AttachmentContext } from "src/util/types/dataModels/attachment"
import { EntityPages } from "src/util/types/dataModels/entityDefinition"
import { IdentifierEntityTarget, IdentifierScanAction } from "src/util/types/dataModels/identifier"
import { Issue, IssueEntity } from "src/util/types/dataModels/issue"
import { Item } from "src/util/types/dataModels/item"
import { ItemClass } from "src/util/types/dataModels/itemClass"
import { ItemModel } from "src/util/types/dataModels/itemModel"
import { WorkOrderCorrectiveItem, WorkOrderRegularItem } from "src/util/types/dataModels/workOrder"
import { OverlayOptions } from "src/util/types/overlays"

/**
 * Creates an overlay dialog with {@link SfxOverlay}, and return the {@link DialogChainObject}.
 *
 * @param options that correspond with the props of {@link SfxOverlay}
 */
export function showOverlay(options: OverlayOptions): DialogChainObject {
  return Dialog.create({
    component: SfxOverlay,
    componentProps: options,
  })
}

//region Special purpose overlays

export function showIdentify(
  scanAction: IdentifierScanAction,
  identifierEntity?: IdentifierEntityTarget,
  customTitle?: string
) {
  const title = getIdentifyTitle(scanAction, customTitle)

  const subtitle = identifierEntity
    ? getEntityWithDesignation(identifierEntity.entityType, identifierEntity.entityName)
    : undefined

  return showOverlay({
    title: title,
    contextSource: ContextSource.IDENTIFY,
    subtitle: subtitle,
    subtitleHtml: Boolean(subtitle),
    pageClasses: ["sfx-identify-overlay__background"],
    hideSeparator: true,
    hideNotifications: true,
    contentComponent: IdentifyOverlay,
    contentComponentProps: { scanAction, identifierEntity },
  })
}

export function showIssueCreate(issueEntity: IssueEntity, unsubmittedWorkId?: string) {
  return Dialog.create({
    component: IssueCreateOverlay,
    componentProps: { issueEntity, unsubmittedWorkId },
  })
}

export function showConfigurations() {
  const { t } = i18n.global
  return showOverlay({
    title: t("userSettings.title_configurations"),
    contextSource: ContextSource.CONFIGURATIONS,
    subtitle: t("userSettings.title"),
    contentComponent: ConfigurationsOverlay,
  })
}

export function showChangePassword() {
  const { t } = i18n.global
  return showOverlay({
    title: t("userSettings.title_change_password"),
    contextSource: ContextSource.CHANGE_PASSWORD,
    subtitle: t("userSettings.title"),
    contentComponent: ChangePasswordOverlay,
  })
}

export function showChangeUnlockKey() {
  const { t } = i18n.global
  return showOverlay({
    title: t("userSettings.title_change_unlock_key"),
    contextSource: ContextSource.CHANGE_UNLOCK_KEY,
    subtitle: t("userSettings.title"),
    contentComponent: ChangeUnlockKeyOverlay,
  })
}

/**
 * Create the model class overlay
 *
 * itemDesignation is mandatory
 * currentItemClass and currentItemModel are both optional
 * Can pass currentItemClass without currentItemModel
 * But currentItemModel must always be passed together with currentItemClass
 *
 * @param itemDesignation
 * @param canGoToOverview
 * @param currentItemClass
 * @param currentItemModel
 */
export function showModelClass(
  itemDesignation: string,
  canGoToOverview = true,
  currentItemClass?: ItemClass,
  currentItemModel?: ItemModel
) {
  return Dialog.create({
    component: ModelClassOverlay,
    componentProps: { itemDesignation, canGoToOverview, currentItemClass, currentItemModel },
  })
}

export function showNotifications() {
  return Dialog.create({
    component: SfxOverlayNotifications,
  })
}

export function showImageView(selectedImage: ImageGalleryItem, images: Array<ImageGalleryItem>) {
  return Dialog.create({
    component: SfxImageViewOverlay,
    componentProps: {
      images,
      selectedIdx: images.findIndex((img) => img.src === selectedImage.src) ?? 0,
    },
  })
}

export async function showItemCreate(copiedItem?: Item): Promise<ItemDocument | null> {
  return new Promise((resolve) =>
    Dialog.create({
      component: ItemCreateOverlay,
      componentProps: { copiedItem },
    })
      .onOk((payload) => resolve(payload))
      .onCancel(() => resolve(null))
  )
}

//endregion

//Manager mode overlays

export function showPhotoGalleryOverlay(
  entityId: string,
  entityType: AttachmentContext,
  disableEditImage?: boolean
): Promise<ImageViewerItem[] | null> {
  const _disableEditImage = disableEditImage || !canUpdateEntityAttachments(entityType)

  return new Promise((resolve) =>
    Dialog.create({
      component: SfxPhotoGalleryOverlay,
      componentProps: { entityId, entityType, disableEditImage: _disableEditImage },
    })
      .onOk((payload) => resolve(payload))
      .onCancel(() => resolve(null))
  )
}

export function showWorkOrderEntityList(
  workOrder: WorkOrderRegularItem | WorkOrderCorrectiveItem,
  progressState: ProgressIndicatorState,
  entitiesIncluded: Item[] | Issue[],
  entitiesPerformed: Item[] | Issue[],
  entity: EntityPages
): Promise<string[] | null> {
  return new Promise((resolve) =>
    Dialog.create({
      component: SfxWorkOrderEntityAssignment,
      componentProps: { workOrder, progressState, entitiesIncluded, entitiesPerformed, entity },
    })
      .onOk((payload: string[]) => resolve(payload))
      .onCancel(() => resolve(null))
  )
}

//endregion
